export const admin = {
    brand: {
        name: "Genos",
        tagline: "Team Collaboration Platform",
        logoAlt: "Genos Logo",
        copyright: "© Genos {year}",
    },
    auth: {
        signIn: {
            title: "Welcome Back",
            newMemberPrompt: "New member?",
            createAccountLink: "Create an account",
            emailLabel: "Email",
            emailPlaceholder: "Enter your email",
            passwordLabel: "Password",
            passwordPlaceholder: "Enter your password",
            rememberMe: "Remember me",
            forgotPassword: "Forgot password?",
            submit: "Sign In",
            orDivider: "or",
            continueWithGoogle: "Continue with Google",
            continueWithGithub: "Continue with GitHub",
            demoButton: "Try with Demo User",
            demoLoading: "Setting up demo…",
            demoHint: "No signup needed — explore the app instantly.",
            demoWarning:
                "Your demo account is automatically deleted after 24 hours (or when you sign out).",
            emailNotVerified: {
                message: "Your email isn't verified yet. Check your inbox for the link.",
                resendButton: "Resend email",
                resending: "Sending…",
                resendSent: "Sent — check your inbox.",
            },
        },
        signUp: {
            title: "Create Account",
            subtitle: "Join us and start your journey",
            nameLabel: "Name",
            namePlaceholder: "Enter your name",
            emailLabel: "Email",
            emailPlaceholder: "Enter your email",
            passwordLabel: "Password",
            passwordPlaceholder: "Create a password",
            confirmPasswordLabel: "Confirm Password",
            confirmPasswordPlaceholder: "Confirm your password",
            submit: "Create Account",
            haveAccountPrompt: "Already have an account?",
            signInLink: "Sign in",
            passwordMismatch: "Passwords do not match.",
            passwordTooWeak: "Password doesn't meet the strength requirements yet.",
            passwordRequirements: {
                minLength: "At least 8 characters",
                uppercase: "One uppercase letter (A–Z)",
                lowercase: "One lowercase letter (a–z)",
                number: "One number (0–9)",
            },
            passwordStrength: {
                weak: "Weak",
                fair: "Fair",
                good: "Good",
                strong: "Strong",
            },
            passwordVisibility: {
                show: "Show password",
                hide: "Hide password",
            },
            afterSubmit: {
                title: "Check your inbox",
                body: "We sent a verification link to {email}. Click it to finish signing up.",
                checkInboxHint: "Click the link in the email to verify your address.",
                resend: "Resend email",
                resending: "Sending…",
                resendSent: "Sent — check your inbox.",
                backToSignIn: "Back to sign in",
            },
            invitedEmailHint: "You're signing up with your invited email.",
        },
        forgotPassword: {
            title: "Forgot Password?",
            body: "Enter the email associated with your account and we'll send you a reset link.",
            emailLabel: "Email",
            emailPlaceholder: "Enter your email",
            submit: "Send reset link",
            successTitle: "Check your inbox",
            successBody:
                "If that email is registered, you'll receive a reset link within a few minutes.",
            close: "Close",
        },
        passwordReset: {
            title: "Set a new password",
            subtitle: "Choose a strong password to finish resetting your account.",
            submit: "Update password",
            successTitle: "Password updated",
            successBody: "Redirecting you to sign in…",
            missingToken:
                "This reset link is invalid or has expired. Request a new one from the sign-in page.",
            invalidToken: "This reset link is invalid or has expired.",
            backToSignIn: "Back to sign in",
        },
        emailVerification: {
            verifyingTitle: "Verifying your email…",
            successTitle: "Your email is verified",
            successBody: "You can now sign in to your account.",
            signInButton: "Sign in",
            invalidTitle: "This link is invalid or has expired",
            invalidBody: "Request a new verification email below.",
            missingTokenTitle: "No verification token found",
            missingTokenBody: "Open the link from your verification email and try again.",
            resendLabel: "Email address",
            resendPlaceholder: "Enter your email",
            resendButton: "Resend verification email",
            resending: "Sending…",
            resendSent: "Sent — check your inbox.",
            backToSignIn: "Back to sign in",
        },
        errors: {
            network: "Network error. Please check your connection and try again.",
            unauthorized: "Unauthorized. Please log in again.",
            generic: "Something went wrong. Please try again later.",
            unexpected: "An unexpected error occurred. Please try again.",
            duplicateEmail: "Please try with a different email.",
            duplicateName: "Please try with a different name.",
            demoRateLimited: "Too many demo sign-ins from this network. Please try again later.",
            demoFailed: "Could not create demo session. Please try again later.",
            tokenMissing: "Unauthorized. Authentication token was not found.",
            teamNotFound: "Team not found. Please check the Team ID and try again.",
            teamNotFoundShort: "Team not found.",
            duplicateTeamName: "Please try with different team name.",
        },
        oauth: {
            failureReasons: {
                email_in_use: "An account already exists for this email address.",
                consent_denied: "OAuth consent was denied. You can try again any time.",
                bad_callback: "OAuth callback was malformed. Please try again.",
                invalid_state:
                    "OAuth state token was invalid or expired. Please start the sign-in flow again.",
                provider_error: "The OAuth provider returned an error. Please try again.",
                not_authenticated: "You need to be signed in to connect a third-party account.",
                already_connected_to_other_user:
                    "This account is already connected to a different user.",
                not_a_login_account:
                    "That account was only connected for calendar access, so it can't sign you in.",
                provider_already_connected:
                    "You already have an account connected for this provider. Disconnect it first.",
                unknown_provider: "Unknown OAuth provider.",
                fallback: "OAuth flow failed.",
            },
            methods: {
                email: "your email and password",
                google: "Google",
                github: "GitHub",
                fallback: "the method you originally signed up with",
            },
            useAnotherMethod:
                "Sign in with {method} instead, or pick a different account on the provider’s screen.",
            apiClientFailed: "Could not initialize the API client.",
            profileFailed: "Could not load your user profile.",
            title: "Sign-in failed",
            backToSignIn: "Back to sign in",
            finishing: "Finishing sign-in…",
        },
    },
    joinTeam: {
        title: "Team Management",
        yourTeams: "Your Teams",
        teamFound: "Team Found!",
        searchByTeamId: "Search by Team ID",
        createNewTeam: "Create New Team",
        teamIdLabel: "Team ID",
        teamIdPlaceholder: "Enter team ID to search",
        teamNameLabel: "Team Name",
        teamNamePlaceholder: "Enter your team name",
        starterOptIn: "Start with a sample project",
        starterOptInHint:
            "A small getting-started project, note, and todos so the app isn't empty — delete it anytime.",
        searchAnother: "Search Another",
        requestToJoin: "Request to Join",
        searchTeam: "Search Team",
        createTeam: "Create Team",
        joinRequestSent: "Request to join the team sent.",
        alreadyJoined: "Already joined",
        hasJoined: "Has joined",
        signOut: "Sign Out",
    },
    teamDropdown: {
        openMenu: "Open Team Menu",
        management: "Team Management",
        showTeamProfile: "Show Team Profile",
        yourTeams: "Your Teams ({count})",
        currentTeam: "Current Team",
        newTeam: "New Team",
        comingSoon: "Coming soon",
    },
    teamProfile: {
        title: "Team Profile - {teamName}",
        editTeamImage: "Edit Team Image",
        teamName: "Team Name",
        teamId: "Team ID",
        owner: "Owner",
        members: "Members ({filtered}/{total})",
        searchMembers: "Search members...",
        noMembersFound: 'No members found matching "{query}"',
        createdDate: "Created Date",
        uploadFailed: "Failed to upload team profile image.",
    },
    // Cross-team sharing, relationship layer. The explainer is load-
    // bearing: a connection grants nothing, and "connect" read as "give
    // them access" is the one misunderstanding that would matter.
    connectedTeams: {
        title: "Connected teams",
        explainer:
            "Connecting lets you share a specific chat, project, or note folder with another organization later. On its own it gives them access to nothing.",
        empty: "Not connected to any other team yet.",
        teamIdPlaceholder: "Paste the other team's ID",
        connectButton: "Request connection",
        approve: "Approve",
        decline: "Decline",
        disconnect: "Disconnect",
        incomingNote: "Wants to connect with your team",
        outgoingNote: "Waiting for them to approve",
        // The one chip on a live connection row, and it always describes
        // THE OTHER TEAM: they invited us, or we invited them. The hints
        // have to send ownership questions to the shared item itself,
        // because this chip deliberately says nothing about it.
        ownerTeam: "Owner",
        ownerTeamHint:
            "They invited your team to connect, and your team approved. Who owns a shared chat, project or note folder is shown on that item.",
        guestTeam: "Guest",
        guestTeamHint:
            "Your team invited them to connect, and they approved. Who owns a shared chat, project or note folder is shown on that item.",
        statusConnected: "Connected",
        statusPending: "Awaiting your owner",
        statusAwaiting: "Awaiting reply",
        disconnectTitle: "Disconnect from this team?",
        disconnectDescription:
            "This ends every chat, project, and note folder shared between you, and removes their people from your data immediately. You can reconnect later.",
        withdrawnNotice: "Removed {count} external participant(s) from your shared items.",
        errorSelf: "That's your own team's ID.",
    },
    inviteMembers: {
        openButton: "Invite members",
        title: "Invite team members",
        subtitle:
            "Enter one or more email addresses. Each person gets an invite link locked to their email.",
        // Guest variant. The promise has to be different, and precise:
        // an outside collaborator is agreeing to join ONE project, and
        // the copy is what sets that expectation before they accept.
        openButtonGuest: "Invite guests",
        titleGuest: "Invite guests to {projectName}",
        subtitleGuest:
            "Guests join this project only. They won't see your other projects, your team's members, or anything else in the workspace.",
        emailPlaceholder: "name@example.com",
        addHint: "Press Enter or comma to add each email.",
        invalidEmail: "That doesn't look like a valid email address.",
        duplicateEmail: "That email is already in the list.",
        send: "Send invites",
        sending: "Sending…",
        inviteMore: "Invite more",
        done: "Done",
        cancel: "Cancel",
        emptyHint: "Add at least one email to send invites.",
        resultsTitle: "Invite results",
        status: {
            sent: "Invite sent",
            already_invited_resent: "Invite resent",
            already_member: "Already a member",
            invalid_email: "Invalid email",
            failed: "Failed to send",
        },
        errors: {
            tokenMissing: "Your session expired. Please sign in again.",
            requestFailed: "Could not send invites. Please try again.",
            unexpected: "An unexpected error occurred. Please try again.",
        },
    },
    acceptInvite: {
        loadingTitle: "Checking your invite…",
        invalidTitle: "This invite is invalid or has expired",
        invalidBody: "Ask the team owner to send you a new invite.",
        mismatchTitle: "Wrong account",
        mismatchBody:
            "You're signed in as {current}, but this invite is for {invited}. Sign out and continue to accept it.",
        mismatchShort: "This invite is for a different email address.",
        signOutAndContinue: "Sign out and continue",
        backToSignIn: "Back to sign in",
    },
    userProfile: {
        myProfile: "My Profile",
        othersProfile: "{userName}'s Profile",
        profileFallback: "Profile",
        editProfileImage: "Edit Profile Image",
        teamName: "Team Name",
        teamId: "Team ID",
        userId: "User ID",
        role: "Role",
        location: "Location",
        localTime: "Local Time",
        phone: "Phone",
        about: "About",
        joinedDate: "Joined Date",
        notAvailable: "N/A",
        phonePlaceholder: "+81 90-1234-5678",
        // Only ever shown to the owner of the profile — for anyone else an
        // unset field renders nothing at all, so this doubles as the
        // affordance that the row is editable.
        phoneNotSet: "Add a phone number",
        locationPlaceholder: "Search for a city",
        locationNotSet: "Add your location",
        locationAuto: "auto",
        locationUseDetected: "Use detected",
        locationDetectedHint: "Detected from your browser ({zone}). Click to set it yourself.",
        // The share toggle, shown only on your own card. `Shared` when your
        // location and local time are visible to teammates; `Hidden` when
        // you've opted out and they see no location row at all.
        locationShared: "Shared with your team",
        locationHidden: "Hidden from your team",
        locationShareHint: "Turn off to hide your location and local time from teammates.",
        aboutPlaceholder: "A few words about you. **Bold**, _italic_, links and lists work.",
        aboutNotSet: "Add a short introduction",
        aboutCounter: "{count} / {max}",
        aboutSaveError: "Couldn't save. Please try again.",
        sameTimeAsYou: "same time as you",
        hoursAheadOfYou: "{count, plural, one {# hour} other {# hours}} ahead of you",
        hoursBehindYou: "{count, plural, one {# hour} other {# hours}} behind you",
        dmToMyself: "DM to myself",
        dmToUser: "DM to {userName}",
        dmToUserFallback: "DM to the user",
        sendDm: "Send DM",
        uploadFailed: "Failed to upload user profile image.",
        // Beside the team name, which for this person is their OWN team —
        // not yours. Without the badge the row reads as a colleague in a
        // team you have never heard of.
        externalBadge: "External",
        externalHint: "Works at another team. You share an object with them.",
    },
    projectProfile: {
        title: "Project Profile - {projectName}",
        editProfileImage: "Edit Profile Image",
        owner: "Owner",
        members: "Members ({filtered}/{total})",
        searchMembers: "Search members...",
        noMembersFound: 'No members found matching "{query}"',
        isPrivate: "Is Private",
        yes: "Yes",
        no: "No",
        createdDate: "Created Date",
        uploadFailed: "Failed to upload user profile image.",
        notAvailable: "N/A",
        projectName: "Project name",
        code: "Code",
        editCode: "Edit code",
        save: "Save",
        cancel: "Cancel",
        codeInvalid: "Use 2–6 letters or digits, starting with a letter.",
        codeSaveFailed: "Could not save the code ({status}).",
        codeNetworkError: "Network error. Please try again.",
    },
    serviceErrors: {
        requestFailed: "Couldn't complete the request. Please try again.",
        requestFailedStatus: "Request failed ({status}).",
        unexpected: "An unexpected error occurred. Please try again.",
    },
    // Team-scoped tags applied to whole PROJECTS, to organize the
    // project list. Distinct from `tasks.*.tags`, which are the
    // per-project tags applied to tasks.
    projectLabels: {
        sectionLabel: "Tags",
        manageTooltip: "Edit tags",
        manageHeading: "Project tags",
        manageDescription:
            "Tags are shared across the team. Tick a tag to put it on this project; editing or deleting one changes it for every project that uses it.",
        newLabelPlaceholder: "New tag name",
        newLabelPreview: "Preview",
        add: "Add",
        empty: "No tags in this team yet. Create the first one above.",
        usedByProjects: "{count, plural, one {Used by # project} other {Used by # projects}}",
        editLabel: "Edit tag",
        deleteLabel: "Delete tag",
        deleteConfirm: "Remove from all projects?",
        assignFailed: "Couldn't update this project's tags.",
        deleteFailed: "Couldn't delete the tag.",
    },
    createTeamModal: {
        title: "Create New Team",
        teamNamePlaceholder: "Unique team name",
        cancel: "Cancel",
        create: "Create",
        creationFailed: "Team Creation Failed",
    },
    status: {
        offline: "Offline",
        offlineForced: "Offline (Forced)",
        online: "Online",
        setOnline: "Set Online",
        setAlwaysOffline: "Set Always Offline",
        updateStatus: "Update Status",
        setStatusPlaceholder: "Set your status…",
        cancel: "CANCEL",
        reset: "RESET",
        set: "SET",
    },
    role: {
        notSet: "Not Set",
        setYourRole: "Set Your Role",
        chooseRole: "Choose a role",
        cancel: "CANCEL",
    },
    guard: {
        loading: "Loading...",
    },
} as const;
